import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Spinner,
  Alert
} from 'reactstrap';
import { createModuleLogger } from '../../utils/logger';
import pdfMemoryManager from '../../utils/PDFMemoryManager';
import './PDFViewerModal.css';

// Crear logger para este componente
const pdfLogger = createModuleLogger('PDFViewerModal');

const PDFViewerModal = ({ isOpen, toggle, ordenId, titulo, filename, forceMobileView = false }) => {
  // Estados del componente
  const [pdfBlob, setPdfBlob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentOrdenId, setCurrentOrdenId] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  
  // Referencias para gestión de memoria
  const iframeRef = useRef(null);
  const currentBlobRef = useRef(null);
  const isUnmountedRef = useRef(false);
  const pdfIdRef = useRef(null); // ID único para este PDF en el gestor de memoria
  
  // Función helper para cleanup usando el gestor de memoria
  const cleanupPdf = (context = 'generic') => {
    if (pdfIdRef.current) {
      const success = pdfMemoryManager.unregisterPDF(pdfIdRef.current);
      pdfLogger.debug(`PDF cleanup via manager - contexto: ${context}`, { 
        pdfId: pdfIdRef.current, 
        success 
      });
      pdfIdRef.current = null;
    }
  };
  
  // Función fallback para cleanup directo (solo en casos de emergencia)
  const fallbackCleanupBlobUrl = (blobUrl, context = 'fallback') => {
    if (blobUrl && typeof URL !== 'undefined' && URL.revokeObjectURL) {
      try {
        URL.revokeObjectURL(blobUrl);
        pdfLogger.debug(`Blob URL limpiada directamente - contexto: ${context}`, { blobUrl });
      } catch (error) {
        pdfLogger.error('Error al limpiar blob URL', { blobUrl, context, error });
      }
    }
  };
  
  // Función para detectar dispositivos móviles
  const detectMobile = () => {
    try {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera || '';
      
      // Detectar iOS específicamente (iPhone, iPad, iPod)
      const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
      
      // Detectar Android específicamente
      const isAndroid = /android/i.test(userAgent);
      
      // Detectar otros dispositivos móviles comunes
      const isMobileUA = /Mobi|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
      
      // Detectar por capacidades touch
      const isTouchDevice = typeof window !== 'undefined' && (
        'ontouchstart' in window || 
        (navigator.maxTouchPoints && navigator.maxTouchPoints > 0) ||
        (window.DocumentTouch && document instanceof window.DocumentTouch)
      );
      
      // Detectar por tamaño de pantalla
      const isSmallScreen = typeof window !== 'undefined' && window.innerWidth <= 768;
      
      // Detectar por orientation API (común en móviles)
      const hasOrientationAPI = typeof window !== 'undefined' && 'orientation' in window;
      
      // Combinar todas las detecciones
      const isMobile = isIOS || isAndroid || isMobileUA || 
                      (isTouchDevice && isSmallScreen) || 
                      (isTouchDevice && hasOrientationAPI);
      
      return isMobile;
    } catch (error) {
      pdfLogger.error('Error al detectar dispositivo móvil', error);
      // En caso de error, asumir escritorio
      return false;
    }
  };
  
  // Función para abrir PDF en dispositivos móviles
  const handleMobileViewPDF = () => {
    if (!pdfBlob) {
      pdfLogger.warn('No hay PDF disponible para abrir en móvil');
      return;
    }
    
    try {
      // Crear una URL temporal para el blob
      const pdfUrl = URL.createObjectURL(pdfBlob);
      
      // Abrir en nueva ventana/pestaña
      const newWindow = window.open(pdfUrl, '_blank');
      
      if (!newWindow) {
        // Si el popup fue bloqueado, intentar descarga directa
        pdfLogger.info('Popup bloqueado, iniciando descarga directa');
        handleMobileDownload();
      } else {
        pdfLogger.info('PDF abierto en nueva ventana para móvil');
        
        // Limpiar URL después de un delay para dar tiempo a que se abra
        setTimeout(() => {
          URL.revokeObjectURL(pdfUrl);
        }, 1000);
      }
    } catch (error) {
      pdfLogger.error('Error al abrir PDF en móvil', error);
      // Fallback a descarga
      handleMobileDownload();
    }
  };
  
  // Función para descargar PDF en dispositivos móviles
  const handleMobileDownload = () => {
    if (!pdfBlob) {
      pdfLogger.warn('No hay PDF disponible para descargar');
      return;
    }
    
    try {
      // Crear URL temporal para la descarga
      const downloadUrl = URL.createObjectURL(pdfBlob);
      
      // Crear elemento de enlace temporal
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `orden_produccion_${ordenId}.pdf`;
      link.style.display = 'none';
      
      // Agregar al DOM, hacer clic y limpiar
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpiar URL temporal
      setTimeout(() => {
        URL.revokeObjectURL(downloadUrl);
      }, 100);
      
      pdfLogger.info('PDF descargado en dispositivo móvil', { ordenId });
    } catch (error) {
      pdfLogger.error('Error al descargar PDF en móvil', { error, ordenId });
    }
  };
  
  // Efecto para detectar dispositivos móviles al montar el componente
  useEffect(() => {
    const mobile = forceMobileView || detectMobile();
    setIsMobile(mobile);
    pdfLogger.debug('Dispositivo detectado', { 
      isMobile: mobile, 
      forceMobileView,
      userAgent: navigator.userAgent,
      screenWidth: window.innerWidth,
      touchSupport: 'ontouchstart' in window
    });
  }, [forceMobileView]);

  // Efecto principal para generar PDF cuando cambian las dependencias
  useEffect(() => {
    if (isOpen && ordenId) {
      // Si el ordenId cambió, limpiar el PDF anterior
      if (currentOrdenId && currentOrdenId !== ordenId) {
        cleanupPdf('ordenId_change');
        setPdfBlob(null);
        setError(null);
      }
      
      // Generar nuevo PDF solo si no existe o cambió el ordenId
      if (!pdfBlob || currentOrdenId !== ordenId) {
        setCurrentOrdenId(ordenId);
        generatePDF();
      }
    }
  }, [isOpen, ordenId]);
  
  // Efecto para cleanup cuando se cierra el modal
  useEffect(() => {
    if (!isOpen) {
      // Marcar PDF como inactivo cuando se cierra el modal
      if (pdfIdRef.current) {
        pdfMemoryManager.markAsInactive(pdfIdRef.current);
      }
      setPdfBlob(null);
      setError(null);
      setCurrentOrdenId(null);
    } else if (isOpen && pdfIdRef.current) {
      // Marcar PDF como activo cuando se abre el modal
      pdfMemoryManager.markAsActive(pdfIdRef.current);
    }
  }, [isOpen]);
  
  // Efecto para cleanup en unmount (safety net)
  useEffect(() => {
    return () => {
      isUnmountedRef.current = true;
      // Limpiar usando el gestor de memoria
      cleanupPdf('component_unmount');
      
      // Fallback directo en caso de emergencia
      if (currentBlobRef.current) {
        fallbackCleanupBlobUrl(currentBlobRef.current, 'component_unmount_fallback');
      }
    };
  }, []);
  
  // Efecto para mantener la referencia del blob actual
  useEffect(() => {
    currentBlobRef.current = pdfBlob;
  }, [pdfBlob]);
  
  // Efecto para event listeners del navegador (safety cleanup)
  useEffect(() => {
    if (!isOpen || !pdfBlob) return;
    
    // Función para cleanup en eventos del navegador
    const handleBrowserCleanup = (event) => {
      pdfLogger.debug(`Evento del navegador detectado: ${event.type} - verificando cleanup`);
      if (pdfIdRef.current) {
        pdfMemoryManager.markAsInactive(pdfIdRef.current);
      }
    };
    
    // Función para cleanup en caso de error global
    const handleError = (event) => {
      pdfLogger.error('Error global detectado - verificando cleanup', { error: event.error });
      if (pdfIdRef.current) {
        pdfMemoryManager.markAsInactive(pdfIdRef.current);
      }
    };
    
    // Función para cleanup en caso de promesa rechazada
    const handleUnhandledRejection = (event) => {
      pdfLogger.error('Promise rechazada no manejada - verificando cleanup', { reason: event.reason });
      if (pdfIdRef.current) {
        pdfMemoryManager.markAsInactive(pdfIdRef.current);
      }
    };
    
    // Event listeners para diferentes eventos del navegador
    const events = [
      'beforeunload',  // Antes de cerrar/recargar página
      'pagehide',      // Cuando la página se oculta
      'visibilitychange' // Cuando cambia la visibilidad
    ];
    
    // Agregar listeners solo si el modal está abierto
    events.forEach(eventType => {
      window.addEventListener(eventType, handleBrowserCleanup, { passive: true });
    });
    
    // Listeners para errores globales
    window.addEventListener('error', handleError, { passive: true });
    window.addEventListener('unhandledrejection', handleUnhandledRejection, { passive: true });
    
    // Cleanup de listeners
    return () => {
      events.forEach(eventType => {
        window.removeEventListener(eventType, handleBrowserCleanup);
      });
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [isOpen, pdfBlob]);
  
  // Efecto para manejar escape key y otras teclas
  useEffect(() => {
    if (!isOpen) return;
    
    const handleKeyDown = (event) => {
      // Escape key para cerrar modal
      if (event.key === 'Escape') {
        pdfLogger.debug('Escape key detectada - cerrando modal');
        handleClose();
      }
      
      // Ctrl+W para cerrar ventana (cleanup preventivo)
      if (event.ctrlKey && event.key === 'w') {
        pdfLogger.debug('Ctrl+W detectado - cleanup preventivo');
        if (pdfIdRef.current) {
          pdfMemoryManager.markAsInactive(pdfIdRef.current);
        }
      }
    };
    
    // Agregar listener para teclado
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);
  
  // Función para generar PDF
  const generatePDF = async () => {
    if (!ordenId || isUnmountedRef.current) return;
    
    setLoading(true);
    setError(null);
    
    // Limpiar PDF anterior si existe
    if (pdfIdRef.current) {
      cleanupPdf('new_pdf_generation');
      setPdfBlob(null);
    }
    
    try {
      // Importación dinámica para evitar dependencias circulares
      const { generatePDFBlob, generatePDFBlobExterna } = await import('../../pages/Ordenes/pdf/pdf_print');
      
      // Verificar si el componente sigue montado antes de continuar
      if (isUnmountedRef.current) {
        pdfLogger.debug('Generación de PDF cancelada - componente desmontado');
        return;
      }
      
      // Determinar si es un PDF externo basándose en el nombre del archivo
      const isExternalPDF = Boolean(filename && typeof filename === 'string' && filename.toLowerCase().includes('externa'));
      const result = isExternalPDF 
        ? await generatePDFBlobExterna(ordenId)
        : await generatePDFBlob(ordenId);
      
      // Verificar nuevamente si el componente sigue montado
      if (isUnmountedRef.current) {
        // Si el componente se desmontó, limpiar el blob generado
        if (result && result.url) {
          fallbackCleanupBlobUrl(result.url, 'generation_after_unmount');
        }
        pdfLogger.debug('PDF generado pero componente desmontado - limpiando blob');
        return;
      }
      
      if (result && result.url && result.blob) {
        // Generar ID único para este PDF
        const pdfId = `pdf_${ordenId}_${Date.now()}`;
        
        // Registrar en el gestor de memoria
        const registered = pdfMemoryManager.registerPDF(pdfId, result.url, result.blob);
        
        if (registered) {
          pdfIdRef.current = pdfId;
          setPdfBlob(result.url);
          pdfLogger.info('PDF generado y registrado exitosamente', { 
            ordenId, 
            filename: result.filename,
            pdfId 
          });
        } else {
          // Si no se pudo registrar (límites excedidos), limpiar y mostrar error
          fallbackCleanupBlobUrl(result.url, 'registration_failed');
          throw new Error('No se pudo cargar el PDF - límite de memoria excedido');
        }
      } else {
        throw new Error('No se pudo generar el PDF');
      }
    } catch (err) {
      // Solo mostrar error si el componente sigue montado
      if (!isUnmountedRef.current) {
        const errorMessage = err.message || 'Error desconocido al generar PDF';
        setError(errorMessage);
        pdfLogger.error('Error al generar PDF', { ordenId, error: err });
      }
    } finally {
      // Solo actualizar estado si el componente sigue montado
      if (!isUnmountedRef.current) {
        setLoading(false);
      }
    }
  };
  
  // Función para reintentar generación
  const handleRetry = () => {
    setError(null);
    generatePDF();
  };
  
  // Función para cerrar modal
  const handleClose = () => {
    toggle();
  };
  
  return (
    <Modal 
      isOpen={isOpen} 
      toggle={handleClose} 
      size="xl" 
      className="pdf-viewer-modal"
      backdrop="static"
      keyboard={false}
    >
      <ModalHeader toggle={handleClose}>
        <div className="d-flex justify-content-between align-items-center w-100">
          <span className="h5 mb-0">{titulo}</span>
        </div>
      </ModalHeader>
      
      <ModalBody className="p-0" style={{ height: '80vh' }}>
        {loading && (
          <div className="pdf-loading-container">
            <Spinner className="pdf-loading-spinner" role="status" />
            <p className="pdf-loading-text">Generando PDF...</p>
          </div>
        )}
        
        {error && (
          <div className="pdf-error-container">
            <i className="fas fa-exclamation-triangle pdf-error-icon"></i>
            <h5 className="pdf-error-title">Error al generar PDF</h5>
            <p className="pdf-error-message">{error}</p>
            <p className="pdf-error-details">Verifique los datos de la orden</p>
            <Button className="pdf-retry-button" onClick={handleRetry}>
              <i className="fas fa-redo"></i> Reintentar
            </Button>
          </div>
        )}
        
        {pdfBlob && !loading && !error && (
          <>
            {isMobile ? (
              // Vista móvil: Mostrar opciones en lugar del iframe
              <div className="pdf-mobile-container">
                <div className="pdf-mobile-content">
                  <i className="fas fa-file-pdf pdf-mobile-icon"></i>
                  <h4 className="pdf-mobile-title">PDF Generado</h4>
                  <p className="pdf-mobile-description">
                    El PDF ha sido generado correctamente. Selecciona una opción para visualizarlo:
                  </p>
                  
                  <div className="pdf-mobile-buttons">
                    <Button 
                      className="pdf-mobile-button btn-primary"
                      onClick={handleMobileViewPDF}
                    >
                      <i className="fas fa-external-link-alt"></i>
                      Abrir PDF
                    </Button>
                    
                    <Button 
                      className="pdf-mobile-button btn-success" 
                      onClick={handleMobileDownload}
                    >
                      <i className="fas fa-download"></i>
                      Descargar PDF
                    </Button>
                  </div>
                  
                  <p className="pdf-mobile-info">
                    <i className="fas fa-info-circle"></i>
                    En dispositivos móviles, el PDF se abrirá en el visor predeterminado
                  </p>
                </div>
              </div>
            ) : (
              // Vista escritorio: iframe tradicional
              <iframe
                ref={iframeRef}
                src={pdfBlob}
                width="100%"
                height="100%"
                title="Vista PDF"
                style={{ border: 'none' }}
                aria-label="Vista previa del PDF de la orden de producción"
              />
            )}
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default PDFViewerModal;
